/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Chip';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';

const ChipManager: ComponentManagerFn<'Chip'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Chip'>(platform, 'Chip'),
    type: 'Chip',
    name: 'Chip',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      label: {
        label: 'Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: toOptions(['filled', 'outlined']),
      },
      color: {
        category: 'Look & Feel',
        label: 'Background Color',
        type: 'ColorExpression',
        placeholder: "e.g. '#FFFFFF'",
        helpText: 'Specify a background color for the component.',
      },
      textColor: {
        category: 'Look & Feel',
        label: 'Text Color',
        type: 'ColorExpression',
        placeholder: "e.g. '#157CEC'",
        helpText: 'Specify a text color for the component.',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: toOptions(['small', 'medium']),
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.label.value,
    category: 'Toolbar',
    icon: { iconName: 'rectangle-landscape' },
    description: `**Overview**<br/>
The Chip component is a compact visual element used to represent attributes, status tags, or interactive inputs. It resembles a small pill or badge containing a text label.
Use this component to display categories, filter selections, or status indicators (e.g., "Active," "Pending," "High Priority") in a space-efficient manner.

**1. Visual Configuration**<br/>
* **Label:** The text displayed inside the chip. Supports expressions for dynamic values (e.g., $.item.status).
* **Variant:** Determines the visual style: Filled (Solid background color - default, more prominent) or Outlined (Transparent background with a border - more subtle).
* **Colors:** You can fully customize the color scheme using ColorExpression (Background Color, Text Color).
* **Size:** Available in Small (compact) or Medium (standard) sizes.

**2. Interactivity**<br/>
* **TriggerOnClick:** The component supports adding a TriggerOnClick child. This turns the static tag into an interactive button, allowing users to click it to perform actions (e.g., clicking a "Filter: Tech" chip to remove the filter).

**3. Best Use Cases**<br/>
* **Status Tags:** Displaying "Paid" (Green) or "Overdue" (Red) on an invoice list.
* **Filters:** Showing active search filters like "Category: Electronics" with the ability to click to dismiss.
* **Tags:** Listing skills on a user profile (e.g., "Javascript," "React," "SQL").
* **Selections:** Representing selected items in a multi-select dropdown.`,
    helpRenders: [
      {
        title: 'Chip',
        description: 'With Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: ['label', 'variant', 'size'],
        item: {
          itemId: 'chip',
          props: defaultValue,
          children: ['triggerOnClick'],
          type: 'Chip',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Tooltip',
        page: SamplePage,
        exampleId: 'e6',
        visibleProps: ['toolTip', 'toolTipPosition', 'toolTipMaxWidth'],
        clearParentChildren: true,
        height: 240,
        item: {
          itemId: 'chip',
          props: {
            ...defaultValue,
            toolTip: {
              type: 'text',
              value: 'Chips can be used to show status',
            },
          },
          children: ['triggerOnClick'],
          type: 'Chip',
          parent: 'columnPanel1',
        },
      },
    ],
  };
};

export default ChipManager;
